import {
  applyPortalPresetDefaultsToFlatMap
} from './portal-presets.js'
import {
  resolveKmzPlaybackUseGlobalHeading,
  resolveKmzPlaybackLocalHeadingField,
  resolveKmzPlaybackGlobalHeadingField,
  applyKmzResolvedHeadingDisplayValuesToMap
} from './portal-kmz-heading-utils.js'
import {
  applyKmzResolvedSpeedDisplayValuesToMap
} from './portal-kmz-speed-utils.js'
import {
  applyKmzResolvedGimbalDisplayValuesToMap
} from './portal-kmz-gimbal-utils.js'
import {
  applyKmzResolvedTurnDisplayValuesToMap
} from './portal-kmz-turn-utils.js'
import {
  applyKmzResolvedStraightLineDisplayValuesToMap
} from './portal-kmz-straight-line-utils.js'
import {
  applyKmzResolvedAltitudeContextDisplayValuesToMap
} from './portal-kmz-altitude-utils.js'

const _ = window._
const L = window.L
const $ = window.jQuery

const getPointHeading = function (point) {
  const useGlobalHeading = String(resolveKmzPlaybackUseGlobalHeading(point, '') || '').trim().toLowerCase()
  const headingRaw = (useGlobalHeading === '1' || useGlobalHeading === 'true')
    ? (
        resolveKmzPlaybackGlobalHeadingField(
          point,
          'waypointHeadingAngle',
          resolveKmzPlaybackLocalHeadingField(point, 'waypointHeadingAngle', _.get(point, 'props.ExtendedData.mis:heading'))
        )
      )
    : resolveKmzPlaybackLocalHeadingField(point, 'waypointHeadingAngle', _.get(point, 'props.ExtendedData.mis:heading'))
  const headingVal = Array.isArray(headingRaw) ? headingRaw[0] : headingRaw
  const heading = parseFloat(headingVal)
  return Number.isNaN(heading) ? null : heading
}

const getWaypointScale = function (point, importMissionState) {
  if (!point || !Number.isFinite(point.idx)) return 1
  const total = (importMissionState && Array.isArray(importMissionState.waypoints)) ? importMissionState.waypoints.length : 0
  if (!total) return 1
  if (point.idx === 0 || point.idx === total - 1) return 1.2
  return 1
}

const buildWaypointIcon = function (point, fillColor, importMissionState) {
  const heading = getPointHeading(point)
  const rotateDeg = (heading === null) ? 0 : (heading - 180)
  const scale = getWaypointScale(point, importMissionState)
  const size = 18 * scale
  const html = '' +
    '<svg width="' + size + '" height="' + size + '" viewBox="0 0 18 18" style="overflow: visible;">' +
      '<g transform="scale(' + scale + ')">' +
        '<polygon class="import-point-tri" points="9,0 1,16 17,16" ' +
          'fill="' + fillColor + '" stroke="#111" stroke-width="2" fill-opacity="0.6" ' +
          'transform="rotate(' + rotateDeg + ' 9 0)"></polygon>' +
      '</g>' +
    '</svg>'
  return L.divIcon({
    className: 'import-point',
    html: html,
    iconSize: [size, size],
    iconAnchor: [9 * scale, 0]
  })
}

const refreshWaypointMarker = function (point, importMissionState, fnGetMarkerStyle, fnSetMarkerStyle) {
  if (!point || !point.marker) return
  const style = point._style || fnGetMarkerStyle(point.marker)
  const fillColor = style.fillColor || '#fff'
  point.marker.setIcon(buildWaypointIcon(point, fillColor, importMissionState))
  if (point._selected || point._isActivePoint) {
    fnSetMarkerStyle(point.marker, { color: '#fff', weight: 3 })
  } else {
    fnSetMarkerStyle(point.marker, style)
  }
}

const setByPath = function (obj, path, value) {
  const parts = path.split('.').filter(Boolean)
  let cur = obj
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]
    if (typeof cur[key] !== 'object' || cur[key] === null) {
      cur[key] = {}
    }
    cur = cur[key]
  }
  cur[parts[parts.length - 1]] = value
}

const flattenProps = function (obj, prefix, out) {
  const base = prefix ? prefix + '.' : ''
  if (Array.isArray(obj)) {
    obj.forEach((val, idx) => {
      if (val !== null && typeof val === 'object') {
        flattenProps(val, base + idx, out)
      } else {
        out.push({ path: base + idx, value: val })
      }
    })
    return
  }
  if (obj !== null && typeof obj === 'object') {
    Object.keys(obj).forEach((key) => {
      if (key.startsWith('__')) return
      const val = obj[key]
      if (val !== null && typeof val === 'object') {
        flattenProps(val, base + key, out)
      } else {
        out.push({ path: base + key, value: val })
      }
    })
  }
}

const propsToMap = function (props) {
  const rows = []
  flattenProps(props, '', rows)
  const map = {}
  rows.forEach((row) => { map[row.path] = row.value })
  return map
}

const hasXmlAncestorTag = function (xmlEl, tagName) {
  let node = xmlEl ? xmlEl.parentNode : null
  while (node && node.tagName) {
    if (node.tagName === tagName) return true
    node = node.parentNode
  }
  return false
}

const buildProjectDocumentClone = function (documentEl) {
  if (!documentEl) return null
  const clone = documentEl.cloneNode(true)
  const folderEls = Array.from(clone.getElementsByTagName('Folder') || [])
  folderEls.forEach((folderEl) => {
    const removablePlacemarks = Array.from(folderEl.children || []).filter((el) => el.tagName === 'Placemark')
    removablePlacemarks.forEach((placemarkEl) => {
      folderEl.removeChild(placemarkEl)
    })
  })
  const lineCoordinates = Array.from(clone.getElementsByTagName('coordinates') || []).filter((coordEl) => {
    return hasXmlAncestorTag(coordEl, 'LineString')
  })
  lineCoordinates.forEach((coordEl) => {
    if (coordEl.parentNode) {
      coordEl.parentNode.removeChild(coordEl)
    }
  })
  return clone
}

const propsMapToRows = function (map, propSortPrefixPriority) {
  if (!map || typeof map !== 'object') return []
  const fnGetPriorityIndex = function (path) {
    const lowerPath = String(path || '').toLowerCase()
    for (let i = 0; i < propSortPrefixPriority.length; i++) {
      const prefix = String(propSortPrefixPriority[i] || '').toLowerCase()
      if (!prefix) continue
      if (lowerPath.indexOf(prefix) === 0) return i
    }
    return -1
  }
  return Object.keys(map)
    .sort((a, b) => {
      const aPriority = fnGetPriorityIndex(a)
      const bPriority = fnGetPriorityIndex(b)
      if (aPriority !== bPriority) {
        if (aPriority === -1) return 1
        if (bPriority === -1) return -1
        return aPriority - bPriority
      }
      return String(a).localeCompare(String(b))
    })
    .map((path) => ({ path: path, value: map[path] }))
}

const sortProjectSettingsRows = function (rows, nonExportPointHeightPath) {
  const list = Array.isArray(rows) ? rows.slice() : []
  const getPriority = function (path) {
    const lower = String(path || '').toLowerCase()
    if (lower === nonExportPointHeightPath) return -1
    if (lower.includes('executeheightmode')) return 1
    if (lower.includes('heightmode')) return 0
    return 2
  }
  return list.sort((a, b) => {
    const pa = getPriority(a && a.path)
    const pb = getPriority(b && b.path)
    if (pa !== pb) return pa - pb
    return 0
  })
}

const sortPointEditorRows = function (rows, nonExportPointHeightPath) {
  const list = Array.isArray(rows) ? rows.slice() : []
  const internalPointNamePath = 'project.pointname'
  const fnNormalizePathForSort = function (path) {
    return String(path || '')
      .toLowerCase()
      .replace(/wpml:/g, '')
      .replace(/\.\d+(?=\.|$)/g, '')
      .trim()
  }
  const pointEditorOrder = [
    internalPointNamePath,
    String(nonExportPointHeightPath || '').toLowerCase(),
    'ellipsoidheight',
    'executeheight',
    'height',
    'useglobalheight',
    'point.coordinates',
    'coordinates.lat',
    'coordinates.lng',
    'coordinates.alt',
    'template.folder.autoflightspeed',
    'template.folder.globalwaypointheadingparam.waypointheadingangle',
    'template.folder.globalwaypointheadingparam.waypointheadingmode',
    'template.folder.globalwaypointheadingparam.waypointheadingpoiindex',
    'template.folder.globalwaypointheadingparam.waypointpoipoint',
    'template.folder.globalwaypointturnmode',
    'template.useglobalheadingparam',
    'template.useglobalspeed',
    'template.useglobalturnparam',
    'waylines.folder.autoflightspeed',
    'waylines.waypointheadingparam.waypointheadingangle',
    'waylines.waypointheadingparam.waypointheadingangleenable',
    'waylines.waypointheadingparam.waypointheadingmode',
    'waylines.waypointheadingparam.waypointheadingpathmode',
    'waylines.waypointheadingparam.waypointheadingpoiindex',
    'waylines.waypointheadingparam.waypointpoipoint',
    'waylines.waypointspeed',
    'waylines.waypointturnparam.waypointturndampingdist',
    'waylines.waypointturnparam.waypointturnmode',
    'index',
    'isrisky',
    'usestraightline',
    'gimbalpitchangle',
    'waypointgimbalheadingparam.waypointgimbalpitchangle',
    'waypointgimbalheadingparam.waypointgimbalyawangle',
    'waypointworktype'
  ]
  const fnResolveOrderPriority = function (path) {
    const normalized = fnNormalizePathForSort(path)
    const idx = pointEditorOrder.indexOf(normalized)
    if (idx !== -1) return idx
    if (normalized.indexOf('coordinates.') === 0) return pointEditorOrder.indexOf('coordinates.alt')
    return pointEditorOrder.length + 100
  }
  const fnIsTemplateGimbalPitchPath = function (path) {
    const normalized = fnNormalizePathForSort(path)
    if (!normalized) return false
    if (normalized.includes('waypointgimbalheadingparam')) return false
    return normalized === 'gimbalpitchangle' || normalized.endsWith('.gimbalpitchangle')
  }
  const fnIsWaylineGimbalPitchPath = function (path) {
    const normalized = fnNormalizePathForSort(path)
    if (!normalized) return false
    return normalized.includes('waypointgimbalheadingparam') && normalized.endsWith('waypointgimbalpitchangle')
  }
  const withIndex = list.map((row, idx) => ({ row: row, idx: idx }))
  const sorted = withIndex
    .sort((a, b) => {
      const priorityA = fnResolveOrderPriority(a && a.row && a.row.path)
      const priorityB = fnResolveOrderPriority(b && b.row && b.row.path)
      if (priorityA !== priorityB) return priorityA - priorityB
      return a.idx - b.idx
    })
    .map((item) => item.row)
  const templatePitchIdx = sorted.findIndex((row) => fnIsTemplateGimbalPitchPath(row && row.path))
  const waylinePitchIdx = sorted.findIndex((row) => fnIsWaylineGimbalPitchPath(row && row.path))
  if (templatePitchIdx !== -1 && waylinePitchIdx !== -1 && templatePitchIdx !== (waylinePitchIdx - 1)) {
    const row = sorted.splice(templatePitchIdx, 1)[0]
    const targetIdx = sorted.findIndex((item) => fnIsWaylineGimbalPitchPath(item && item.path))
    if (targetIdx !== -1) {
      sorted.splice(targetIdx, 0, row)
    } else {
      sorted.push(row)
    }
  }
  return sorted
}

const templateSupportsPointCoordinatesField = function (fnGetTemplateWaypointDefaults) {
  const defaults = applyPortalPresetDefaultsToFlatMap(fnGetTemplateWaypointDefaults())
  return !!(defaults && Object.prototype.hasOwnProperty.call(defaults, 'Point.coordinates'))
}

const mergePropsForEdit = function (points, importMissionType, nonExportPointHeightPath, fnGetTemplateWaypointDefaults) {
  const merged = {}
  if (!points.length) return merged
  const templateDefaults = applyPortalPresetDefaultsToFlatMap(fnGetTemplateWaypointDefaults())
  const maps = points.map((pt) => {
    const map = propsToMap((pt && pt.props) || {})
    if (importMissionType === 'kmz') {
      applyKmzResolvedHeadingDisplayValuesToMap(map, pt, 'playback')
      applyKmzResolvedSpeedDisplayValuesToMap(map, pt, 'playback')
      applyKmzResolvedGimbalDisplayValuesToMap(map, pt, 'playback')
      applyKmzResolvedTurnDisplayValuesToMap(map, pt, 'playback')
      applyKmzResolvedStraightLineDisplayValuesToMap(map, pt, 'playback')
      applyKmzResolvedAltitudeContextDisplayValuesToMap(map, pt, 'playback')
    }
    return map
  })
  const extraKeys = (importMissionType === 'kmz')
    ? ['project.pointName', nonExportPointHeightPath]
    : ['project.pointName', 'Point.altitudeMode', nonExportPointHeightPath]
  const allKeys = _.uniq(_.flatten(maps.map(m => Object.keys(m))).concat(Object.keys(templateDefaults)).concat(extraKeys))
  allKeys.forEach((key) => {
    const presentValues = maps
      .filter(m => Object.prototype.hasOwnProperty.call(m, key))
      .map(m => m[key])
    if (!presentValues.length) {
      merged[key] = Object.prototype.hasOwnProperty.call(templateDefaults, key) ? templateDefaults[key] : ''
      return
    }
    const values = maps.map(m => (Object.prototype.hasOwnProperty.call(m, key) ? m[key] : undefined))
    const first = values[0]
    const same = values.every(v => _.isEqual(v, first))
    merged[key] = same ? first : ''
  })
  return merged
}

const filterPointEditorMapForMissionType = function (map, importMissionType, fnGetTemplateWaypointDefaults, fnTemplateSupportsPointCoordinatesField) {
  const safeMap = _.assign({}, map || {})
  const fnNormalizePointEditorPath = function (path) {
    return String(path || '')
      .toLowerCase()
      .replace(/wpml:/g, '')
      .replace(/\.\d+(?=\.|$)/g, '')
      .trim()
  }
  const fnIsKmzProjectSettingsPath = function (path) {
    const normalized = fnNormalizePointEditorPath(path)
    if (!normalized) return false
    if (normalized.indexOf('template.folder.') === 0 || normalized.indexOf('waylines.folder.') === 0 || normalized.indexOf('folder.') === 0) {
      return true
    }
    if (normalized.indexOf('waylinecoordinatesysparam.') === 0) return true
    if (normalized.indexOf('globalwaypointheadingparam.') === 0) return true
    if (normalized.indexOf('payloadparam.') === 0) return true
    return normalized === 'templatetype' ||
      normalized === 'coordinatemode' ||
      normalized === 'heightmode' ||
      normalized === 'positioningtype' ||
      normalized === 'autoflightspeed' ||
      normalized === 'globalheight' ||
      normalized === 'gimbalpitchmode' ||
      normalized === 'globalwaypointturnmode' ||
      normalized === 'globalusestraightline' ||
      normalized === 'califlightenable' ||
      normalized === 'executeheightmode'
  }
  const fnIsKmzInternalScopedPointPath = function (path) {
    const normalized = fnNormalizePointEditorPath(path)
    if (!normalized) return false
    return normalized === 'template.useglobalheight' ||
      normalized === 'waylines.useglobalheight' ||
      normalized === 'template.usestraightline' ||
      normalized === 'waylines.usestraightline'
  }
  const fnIsKmzEffectivePointDisplayPath = function (path) {
    const normalized = fnNormalizePointEditorPath(path)
    return normalized === 'useglobalheight' || normalized === 'usestraightline'
  }

  if (importMissionType === 'kmz') {
    const hasWpmlIndex = Object.prototype.hasOwnProperty.call(safeMap, 'wpml:index')
    const hasPlainIndex = Object.prototype.hasOwnProperty.call(safeMap, 'index')
    if (hasWpmlIndex && hasPlainIndex) {
      delete safeMap.index
    } else if (!hasWpmlIndex && hasPlainIndex) {
      safeMap['wpml:index'] = safeMap.index
      delete safeMap.index
    }
  }

  const templateDefaults = applyPortalPresetDefaultsToFlatMap(fnGetTemplateWaypointDefaults())
  const templatePaths = new Set(Object.keys(templateDefaults || {}))
  if (templatePaths.has('wpml:index')) templatePaths.add('index')
  if (templatePaths.has('index')) templatePaths.add('wpml:index')
  Object.keys(safeMap).forEach((rawPath) => {
    const path = String(rawPath || '')
    if (!path) {
      delete safeMap[rawPath]
      return
    }
    const isActionPath = /^ExtendedData\.mis:actions(\.|$)/.test(path) || /^wpml:actionGroup(\.|$)/.test(path)
    const isProjectPath = /^project\./i.test(path)
    const isSyntheticCoordinatesPath = /^coordinates\./.test(path)
    const isTemplatePath = templatePaths.has(path)
    const isKmzProjectSettingsPath = importMissionType === 'kmz' && fnIsKmzProjectSettingsPath(path)
    const isKmzInternalScopedPointPath = importMissionType === 'kmz' && fnIsKmzInternalScopedPointPath(path)
    const isKmzEffectivePointDisplayPath = importMissionType === 'kmz' && fnIsKmzEffectivePointDisplayPath(path)
    const keepPath = !isKmzProjectSettingsPath &&
      !isKmzInternalScopedPointPath &&
      (isProjectPath || isActionPath || isKmzEffectivePointDisplayPath || (isTemplatePath && !isSyntheticCoordinatesPath))
    if (!keepPath) {
      delete safeMap[rawPath]
    }
  })

  if (!fnTemplateSupportsPointCoordinatesField()) {
    delete safeMap['Point.coordinates']
  }
  return safeMap
}

const updateMissionLineCoords = function (importMissionState) {
  if (!importMissionState || !importMissionState.importCoordEl) return
  const coords = (importMissionState.waypoints || []).map((pt) => {
    const alt = (pt && pt.altitude !== null && pt.altitude !== undefined) ? pt.altitude : (_.get(pt, 'props.coordinates.alt', null))
    const pair = String(pt.latlng[1]) + ',' + String(pt.latlng[0])
    return (alt === null || alt === undefined || alt === '') ? pair : (pair + ',' + String(alt))
  })
  importMissionState.importCoordEl.textContent = coords.join(' ')
}

const refreshImportLine = function (importMissionState, fnUpdateMissionLineCoords, fnScheduleRoute3dSharedSync) {
  if (!importMissionState || !importMissionState.polyline) return
  const coords = (importMissionState.waypoints || []).map(pt => pt.latlng)
  importMissionState.polyline.setLatLngs(coords)
  fnUpdateMissionLineCoords()
  fnScheduleRoute3dSharedSync()
}

const syncAddPointCursor = function (importMissionState, importAddPointMode) {
  if (!importMissionState || !importMissionState.polyline) return
  const pathEl = importMissionState.polyline._path
  if (!pathEl) return
  $(pathEl).toggleClass('portal-import-line--add-point-cursor', !!importAddPointMode)
}

const setWaypointDragging = function (importMissionState, enabled) {
  if (!importMissionState || !importMissionState.waypoints) return
  importMissionState.waypoints.forEach((pt) => {
    if (pt.marker && pt.marker.dragging) {
      if (enabled) {
        pt.marker.dragging.enable()
      } else {
        pt.marker.dragging.disable()
      }
    }
  })
}

const buildPointCoordinatesText = function (props, importMissionType) {
  const coords = (props && props.coordinates) ? props.coordinates : {}
  const lng = (coords && coords.lng !== undefined && coords.lng !== null) ? String(coords.lng) : ''
  const lat = (coords && coords.lat !== undefined && coords.lat !== null) ? String(coords.lat) : ''
  if (importMissionType === 'kmz') {
    return lng + ',' + lat
  }
  const alt = (coords && coords.alt !== undefined && coords.alt !== null) ? String(coords.alt).trim() : ''
  return alt ? (lng + ',' + lat + ',' + alt) : (lng + ',' + lat)
}

export {
  getPointHeading,
  getWaypointScale,
  buildWaypointIcon,
  refreshWaypointMarker,
  setByPath,
  flattenProps,
  propsToMap,
  hasXmlAncestorTag,
  buildProjectDocumentClone,
  propsMapToRows,
  sortProjectSettingsRows,
  sortPointEditorRows,
  templateSupportsPointCoordinatesField,
  mergePropsForEdit,
  filterPointEditorMapForMissionType,
  updateMissionLineCoords,
  refreshImportLine,
  syncAddPointCursor,
  setWaypointDragging,
  buildPointCoordinatesText
}
